// Piso 2 (y la lupa de cuentas) de la Sala de Control. Una pregunta por caja, pocas cifras, la
// explicación en la «i». Maquetas aprobadas por Pau el 30-09.
import { Search } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso } from '../ui';
import { delta, dinero, dineroExacto, edad, miles, numero, SIN_VALOR, ZONA_SALA } from './formato';
import { isoDe, seleccionDe } from './fechas';
import { BarrasDia, HoverDia } from './GraficosDia';
import { useSalaRango } from './useSalaRango';
import { BarrasH, BarrasHora, Caja, Calculando, Leyenda, LineasHora, SinDatos, Tarjeta, VerTodas } from './piezas';
import type {
  ClaseEscalacion,
  CuentasSala,
  DineroSala,
  FansSala,
  Freno,
  CuentaSala,
  MaquinaSala,
  NombreRinconSala,
  SeguridadSala,
} from './tipos';
import { salaApi } from './api';
import { Cerebros } from './Cerebros';
import { colorModelo, nombreModelo } from './modelos';
import { Gasto } from './Gasto';
import { Obra } from './Obra';
import { Taller } from './Taller';
import { calculandoPrimeraVez, useSalaRincon } from './useSalaRincon';

const pct = (x: number, d = 1) => `${numero(x, d)} %`;
const de = (a: number, b: number) => (b > 0 ? (100 * a) / b : 0);

function Carga<T>({ nombre, children }: { nombre: NombreRinconSala; children: (datos: T, referencia: Date) => ReactNode }) {
  const { foto, fallo } = useSalaRincon<T>(nombre);
  if (fallo && !foto) return <Aviso tono="error">{t('salaControl.errorCarga')}</Aviso>;
  if (calculandoPrimeraVez(foto)) return <Calculando />;
  if (!foto) return null;
  const problema = foto.error ? t(`salaControl.rincones.errores.${foto.error.mensaje}` as TKey) : null;
  if (!foto.datos) {
    return problema ? <Aviso tono="error">{t('salaControl.rincones.sinDatosPorError', { motivo: problema })}</Aviso> : <SinDatos texto={t('salaControl.rincones.sinDatos')} />;
  }
  const desfasada = foto.vieja || !!foto.error;
  return (
    <div className="flex flex-col gap-3">
      {desfasada ? (
        <Aviso tono="aviso">
          {t('salaControl.rincones.desfasado', { edad: edad(foto.edad_s ?? 0), motivo: problema ?? t('salaControl.rincones.errores.error') })}
        </Aviso>
      ) : null}
      <div className={cn(desfasada && 'opacity-60')}>{children(foto.datos, foto.generado_at ? new Date(foto.generado_at) : new Date())}</div>
      {foto.edad_s !== null ? <p className="text-right text-o-nota text-oficina-texto3">{t('salaControl.rincones.edad', { edad: edad(foto.edad_s) })}</p> : null}
    </div>
  );
}

// ── Dinero ──
function Dinero() {
  return (
    <Carga<DineroSala> nombre="dinero">
      {(d, ref) => (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
            <Tarjeta titulo={t('salaControl.rincones.dinero.ventasHoy')} valor={dinero(d.desglose.ppv + d.desglose.propinas)} exacto={dineroExacto(d.desglose.ppv + d.desglose.propinas)} tono="bueno" />
            <Tarjeta titulo={t('salaControl.rincones.dinero.ppv')} valor={dinero(d.desglose.ppv)} exacto={dineroExacto(d.desglose.ppv)} detalle={t('salaControl.rincones.dinero.compras', { n: numero(d.desglose.compras) })} />
            <Tarjeta titulo={t('salaControl.rincones.dinero.propinas')} valor={dinero(d.desglose.propinas)} exacto={dineroExacto(d.desglose.propinas)} />
            <Tarjeta titulo={t('salaControl.rincones.dinero.enChats')} valor={dinero(d.enChatsDeCalypso)} exacto={dineroExacto(d.enChatsDeCalypso)} ayuda={t('salaControl.rincones.dinero.enChatsAyuda')} />
          </div>
          {d.internas && d.internas.cuentas > 0 ? (
            <p className="px-1 text-o-sub text-oficina-texto3">{t('salaControl.rincones.dinero.internas', { ventas: dinero(d.internas.ventas), n: numero(d.internas.cuentas) })}</p>
          ) : null}
          <Caja
            titulo={t('salaControl.rincones.dinero.porHora')}
            derecha={
              <Leyenda
                items={[
                  { texto: t('salaControl.rincones.dinero.porHoraLeyendaHoy'), clase: 'bg-oficina-marca-oscura' },
                  { texto: t('salaControl.rincones.dinero.porHoraLeyendaAyer'), clase: 'bg-oficina-texto3' },
                ]}
              />
            }
          >
            <BarrasHora barras={d.porHora.hoy} linea={d.porHora.ayer} referencia={ref} etiqueta={t('salaControl.rincones.dinero.porHora')} />
          </Caja>
          <Caja titulo={t('salaControl.rincones.dinero.precios')} sub={t('salaControl.rincones.dinero.preciosSub')}>
            <BarrasH filas={d.precios.map((p) => ({ etiqueta: `${p.tramo} $`, valor: p.unlock }))} formato={(v) => pct(v)} />
          </Caja>
        </div>
      )}
    </Carga>
  );
}

// ── Fans ──
function Fans() {
  return (
    <Carga<FansSala> nombre="fans">
      {(f, ref) => {
        const e = f.embudo;
        const pasos = [
          { texto: t('salaControl.rincones.fans.atendidos'), n: e.atendidos, clase: 'bg-oficina-esperando', nota: '' },
          { texto: t('salaControl.rincones.fans.conPpv'), n: e.conPpv, clase: 'bg-oficina-te-necesita', nota: pct(de(e.conPpv, e.atendidos), 0) },
          { texto: t('salaControl.rincones.fans.compran'), n: e.compran, clase: 'bg-oficina-trabajando', nota: t('salaControl.rincones.fans.deLosQueReciben', { p: pct(de(e.compran, e.conPpv)) }) },
          { texto: t('salaControl.rincones.fans.repiten'), n: e.repiten, clase: 'bg-oficina-marca', nota: t('salaControl.rincones.fans.deLosQueCompran', { p: pct(de(e.repiten, e.compran), 0) }) },
        ];
        const hoy = f.hoy?.hoy ?? null;
        const ayer = f.hoy?.ayer ?? null;
        const dChatean = hoy && ayer ? delta(hoy.chatean, ayer.chatean, false) : null;
        const dPagan = hoy && ayer ? delta(hoy.pagan, ayer.pagan, false) : null;
        const vs = (d: typeof dChatean) => (d && d.direccion !== 'igual' ? `${t('salaControl.ayer')} ${d.direccion === 'sube' ? '↑' : '↓'} ${d.texto}` : undefined);
        return (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2.5">
              <Tarjeta titulo={t('salaControl.rincones.fans.chatean')} valor={hoy ? numero(Math.round(hoy.chatean)) : SIN_VALOR} detalle={vs(dChatean)} />
              <Tarjeta titulo={t('salaControl.rincones.fans.pagan')} valor={hoy ? numero(Math.round(hoy.pagan)) : SIN_VALOR} detalle={vs(dPagan)} tono="bueno" />
            </div>
            <Caja titulo={t('salaControl.rincones.fans.embudo')} sub={t('salaControl.rincones.fans.embudoSub')}>
              <div className="flex flex-col gap-1.5">
                {pasos.map((p, i) => (
                  <div key={p.texto}>
                    <div className="grid grid-cols-[14rem_1fr_12rem] items-center gap-4">
                      <span className="text-o-texto text-oficina-texto2">{p.texto}</span>
                      <span className={cn('flex h-10 items-center rounded-o-s px-3 text-o-cuerpo font-semibold text-oficina-fondo', p.clase)} style={{ width: `${Math.max(8, 100 * Math.sqrt(p.n / Math.max(1, e.atendidos)))}%` }}>
                        {numero(p.n)}
                      </span>
                      <span className="text-o-sub text-oficina-texto2">{p.nota}</span>
                    </div>
                    {i === 0 && e.minHastaPpv !== null ? <p className="ml-[15rem] mt-1 text-o-sub text-oficina-enlace">{t('salaControl.rincones.fans.minPpv', { min: numero(e.minHastaPpv) })}</p> : null}
                    {i === 1 && e.minHastaCompra !== null ? <p className="ml-[15rem] mt-1 text-o-sub text-oficina-enlace">{t('salaControl.rincones.fans.minCompra', { min: numero(e.minHastaCompra) })}</p> : null}
                  </div>
                ))}
              </div>
            </Caja>
            <FansSemana referencia={ref} hoy={hoy} />
          </div>
        );
      }}
    </Carga>
  );
}

/** Los fans que chatean y los que pagan, cada día de los últimos 7 días cerrados, con hoy rayado al final (Pau 10-10:
 *  «si es útil, pon debajo cómo fueron estos números los últimos 7 días»). Sale del rango del Pulso (/pulso/rango),
 *  que cuenta los fans igual que las tarjetas de arriba; el recorrido del embudo no tiene historia por día. */
function FansSemana({ referencia, hoy }: { referencia: Date; hoy: { chatean: number; pagan: number } | null }) {
  const s = seleccionDe('7', isoDe(referencia.getTime()));
  const { rango } = useSalaRango(s.desde, s.hasta);
  if (!rango?.serie.length) return null;
  const dias = rango.serie.map((x) => x.dia);
  const graficas = [
    { titulo: t('salaControl.rincones.fans.chateanDia'), valores: rango.serie.map((x) => x.fans), banda: rango.bandas.fans, hoy: hoy?.chatean ?? null },
    { titulo: t('salaControl.rincones.fans.paganDia'), valores: rango.serie.map((x) => x.pagan), banda: undefined, hoy: hoy?.pagan ?? null },
  ];
  return (
    <Caja titulo={t('salaControl.rincones.fans.semana')} sub={t('salaControl.rincones.fans.semanaSub')}>
      <HoverDia>
        <div className="grid gap-4 md:grid-cols-2">
          {graficas.map((g) => (
            <div key={g.titulo}>
              <p className="mb-1 text-o-sub text-oficina-texto2">{g.titulo}</p>
              <div className="h-[190px]">
                <BarrasDia
                  dias={dias}
                  valores={g.valores}
                  banda={g.banda}
                  formato={(v) => numero(v)}
                  formatoEje={(v) => (v >= 1000 ? `${miles(v)} k` : numero(v))}
                  etiqueta={g.titulo}
                  conMedia={false}
                  hoyRayado={g.hoy === null ? null : Math.round(g.hoy)}
                />
              </div>
            </div>
          ))}
        </div>
      </HoverDia>
    </Caja>
  );
}

// ── Seguridad ──
const CLASES: ClaseEscalacion[] = ['crisis', 'amenaza_creadora', 'menor', 'otro'];
const FRENOS: Freno[] = ['rewrite_performed', 'freno_candados', 'of_gate_hit', 'safety_block', 'fan_blocklist', 'crisis_candado_venta_por_fan', 'refusal_detected'];

function Seguridad() {
  return (
    <Carga<SeguridadSala> nombre="seguridad">
      {(s) => {
        const total = (k: 'hoy' | 'ayer' | 'sem') => CLASES.reduce((a, c) => a + s.escalaciones[c][k], 0);
        return (
          <div className="flex flex-col gap-4">
          {s.clasesDesconocidas.length ? <Aviso tono="aviso">{t('salaControl.rincones.seguridad.desconocidas', { lista: s.clasesDesconocidas.join(', ') })}</Aviso> : null}
          <div className="grid gap-4 xl:grid-cols-[minmax(0,26rem)_1fr]">
            <Caja titulo={t('salaControl.rincones.seguridad.escalaciones')} sub={t('salaControl.rincones.seguridad.escalacionesSub')}>
              <table className="w-full text-o-texto">
                <thead>
                  <tr className="text-o-nota text-oficina-texto3">
                    <th />
                    <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rincones.seguridad.hoy')}</th>
                    <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rincones.seguridad.ayer')}</th>
                    <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rincones.seguridad.sem')}</th>
                  </tr>
                </thead>
                <tbody>
                  {CLASES.map((c) => (
                    <tr key={c} className="border-t border-oficina-linea">
                      <td className="py-2">{t(`salaControl.rincones.seguridad.clases.${c}` as TKey)}</td>
                      <td className="px-2 text-right font-semibold">{numero(s.escalaciones[c].hoy)}</td>
                      <td className="px-2 text-right">{numero(s.escalaciones[c].ayer)}</td>
                      <td className="px-2 text-right">{numero(s.escalaciones[c].sem)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-oficina-linea font-semibold">
                    <td className="py-2">{t('salaControl.rincones.seguridad.total')}</td>
                    <td className="px-2 text-right">{numero(total('hoy'))}</td>
                    <td className="px-2 text-right font-normal">{numero(total('ayer'))}</td>
                    <td className="px-2 text-right font-normal">{numero(total('sem'))}</td>
                  </tr>
                </tbody>
              </table>
            </Caja>
            <Caja titulo={t('salaControl.rincones.seguridad.frenos')} sub={t('salaControl.rincones.seguridad.frenosSub')}>
              <BarrasH filas={FRENOS.map((f) => ({ etiqueta: t(`salaControl.rincones.seguridad.tipos.${f}` as TKey), valor: s.frenos[f], clase: 'bg-oficina-cerrando' }))} formato={(v) => numero(v)} />
            </Caja>
          </div>
          </div>
        );
      }}
    </Carga>
  );
}

// ── Máquina ──
function Maquina() {
  return (
    <Carga<MaquinaSala> nombre="maquina">
      {(m, ref) => {
        const segs = (x: number | null, d = 0) => (x === null ? SIN_VALOR : `${numero(x, d)} s`);
        const modelos = m.modelos.map((x) => x.modelo).filter((x) => x in m.porHora);
        return (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
              <Tarjeta titulo={t('salaControl.rincones.maquina.p95')} valor={segs(m.hoy.p95)} exacto={m.hoy.p95 === null ? undefined : segs(m.hoy.p95, 1)} detalle={t('salaControl.rincones.maquina.objetivo')} tono={m.hoy.p95 !== null && m.hoy.p95 > 30 ? 'malo' : 'bueno'} />
              <Tarjeta titulo={t('salaControl.rincones.maquina.p50')} valor={segs(m.hoy.p50, 1)} />
              <Tarjeta titulo={t('salaControl.rincones.maquina.respaldo')} valor={m.hoy.respaldoPct === null ? SIN_VALOR : pct(m.hoy.respaldoPct)} />
              <Tarjeta titulo={t('salaControl.rincones.maquina.coste')} valor={dinero(m.hoy.costeApoyo)} exacto={dineroExacto(m.hoy.costeApoyo)} ayuda={t('salaControl.rincones.maquina.costeAyuda')} />
            </div>
            <div className="flex flex-col gap-4">
              <Caja
                titulo={t('salaControl.rincones.maquina.porHora')}
                sub={t('salaControl.rincones.maquina.porHoraSub')}
                derecha={<Leyenda items={modelos.map((x) => ({ texto: nombreModelo(x), clase: colorModelo(x, modelos).fondo }))} />}
              >
                <LineasHora
                  series={modelos.map((x) => {
                    const c = colorModelo(x, modelos);
                    return { valores: m.porHora[x] ?? [], clase: c.trazo, marca: c.fondo, punto: c.punto, nombre: nombreModelo(x) };
                  })}
                  objetivo={{ valor: 30, texto: t('salaControl.rincones.maquina.objetivo') }}
                  referencia={ref}
                  etiqueta={t('salaControl.rincones.maquina.porHora')}
                  formatoY={(v) => `${numero(v)} s`}
                  formato={(v) => `${numero(v, 1)} s`}
                />
              </Caja>
              <Caja titulo={t('salaControl.rincones.maquina.tabla')}>
                <table className="w-full text-o-texto">
                  <thead>
                    <tr className="text-o-nota text-oficina-texto3">
                      <th />
                      <th className="px-1.5 py-1.5 text-right font-medium">{t('salaControl.rincones.maquina.respuestas')}</th>
                      <th className="px-1.5 py-1.5 text-right font-medium">{t('salaControl.rincones.maquina.tipica')}</th>
                      <th className="px-1.5 py-1.5 text-right font-medium">{t('salaControl.rincones.maquina.lento')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.modelos.map((x) => (
                      <tr key={x.modelo} className="border-t border-oficina-linea">
                        <td className="py-2" title={x.modelo}>
                          <span className={cn('mr-2 inline-block h-2 w-2 rounded-full', colorModelo(x.modelo, modelos).fondo)} />
                          {nombreModelo(x.modelo)}
                        </td>
                        <td className="px-1.5 text-right">{numero(x.respuestas)}</td>
                        <td className="px-1.5 text-right">{segs(x.p50)}</td>
                        <td className={cn('px-1.5 text-right font-semibold', x.p95 > 30 && 'text-oficina-error')}>{segs(x.p95)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Caja>
            </div>
          </div>
        );
      }}
    </Carga>
  );
}

// ── Cuentas y lupa ──
function horaCorta(iso: string | null): string {
  if (!iso) return SIN_VALOR;
  return new Date(`${iso.replace(' ', 'T')}Z`).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: ZONA_SALA });
}

function Cuentas() {
  return <Carga<CuentasSala> nombre="cuentas">{(c) => <CuentasVista c={c} />}</Carga>;
}

/** La lupa: busca en el servidor (sobre su última foto, sin consultar nada) con una pausa al teclear. */
function useBuscaCuentas(q: string): CuentaSala[] | null {
  const [res, setRes] = useState<CuentaSala[] | null>(null);
  useEffect(() => {
    const texto = q.trim();
    if (!texto) {
      setRes(null);
      return;
    }
    let vivo = true;
    const id = setTimeout(() => {
      salaApi
        .buscarCuentas(texto)
        .then((r) => vivo && setRes(r.cuentas))
        .catch(() => vivo && setRes([]));
    }, 250);
    return () => {
      vivo = false;
      clearTimeout(id);
    };
  }, [q]);
  return res;
}

function nombreCuenta(x: CuentaSala): string {
  return x.nombre ?? x.usuario ?? x.id.slice(0, 8);
}

function ListaCuentas({ titulo, sub, cuentas, total }: { titulo: string; sub: string; cuentas: CuentaSala[]; total: number }) {
  if (!cuentas.length) return null;
  return (
    <Caja titulo={titulo} sub={sub}>
      <div className="flex flex-wrap gap-2">
        {cuentas.slice(0, 20).map((x) => (
          <span key={x.id} className="flex items-center gap-2 rounded-full bg-oficina-fondo px-3 py-1.5 text-o-sub">
            <span className={cn('h-2 w-2 rounded-full', x.parada ? 'bg-oficina-error' : 'bg-oficina-bloqueado')} />
            {nombreCuenta(x)}
            <span className="text-oficina-texto3">{horaCorta(x.ultimaRespuesta)}</span>
          </span>
        ))}
        {total > 20 ? <span className="px-2 py-1.5 text-o-sub text-oficina-texto3">{t('salaControl.rincones.cuentas.masCuentas', { n: numero(total - 20) })}</span> : null}
      </div>
    </Caja>
  );
}

const FILAS_A_LA_VISTA = 12;
/** Columna de cifra: al ancho justo de su contenido, con el mismo aire a los lados. */
const CIFRA = 'w-px whitespace-nowrap px-3 tabular-nums';

function CuentasVista({ c }: { c: CuentasSala }) {
  const [busca, setBusca] = useState('');
  const encontradas = useBuscaCuentas(busca);
  const [todas, setTodas] = useState(false);
  const filas = encontradas ?? c.top;
  // 12 a la vista y «Ver las N» (Pau 10-10: antes enseñaba 12 de las 25 sin decirlo); lo que encuentra la lupa, entero.
  const recortada = !encontradas && !todas && filas.length > FILAS_A_LA_VISTA;
  const visibles = recortada ? filas.slice(0, FILAS_A_LA_VISTA) : filas;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
        <Tarjeta titulo={t('salaControl.rincones.cuentas.total')} valor={numero(c.total)} />
        <Tarjeta titulo={t('salaControl.rincones.cuentas.trabajando')} valor={numero(c.trabajando)} ayuda={t('salaControl.rincones.cuentas.trabajandoAyuda')} tono="bueno" />
        <Tarjeta
          titulo={t('salaControl.rincones.cuentas.calladas')}
          valor={`${numero(c.calladas)} · ${numero(c.paradas)}`}
          detalle={t('salaControl.rincones.cuentas.calladasDetalle')}
          ayuda={t('salaControl.rincones.cuentas.calladasAyuda')}
          tono={c.calladas || c.paradas ? 'aviso' : 'neutro'}
        />
        <Tarjeta
          titulo={t('salaControl.rincones.cuentas.experimento')}
          valor={c.experimento.map((b) => nombreModelo(b.modelo)).join(' / ') || SIN_VALOR}
          texto
          detalle={c.experimento.map((b) => `${numero(b.fans)} ${t('salaControl.rincones.cuentas.fans').toLowerCase()}`).join(' / ')}
        />
      </div>

      <Caja titulo={t('salaControl.pestanas.cuentas')}>
        <label className="mb-3 flex items-center gap-2 rounded-o-s bg-oficina-fondo px-3 py-2 text-o-texto">
          <Search className="h-4 w-4 text-oficina-texto3" aria-hidden />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={t('salaControl.rincones.cuentas.buscar')}
            aria-label={t('salaControl.rincones.cuentas.buscar')}
            className="w-full bg-transparent outline-none placeholder:text-oficina-texto3"
          />
        </label>
        {filas.length ? (
          <>
          {/* 10-10 (Pau: «un hueco grande entre Fans y Última respuesta»): la cuenta se lleva el sitio que sobra y las
              cifras van juntas, cada una al ancho justo con el mismo espacio entre ellas; la hora, centrada bajo su
              título (que es más ancho que «19:06»). */}
          <table className="w-full text-o-texto">
            <thead>
              <tr className="text-o-nota text-oficina-texto3">
                <th className="py-1.5 text-left font-medium">{t('salaControl.rincones.cuentas.cuenta')}</th>
                <th className={cn(CIFRA, 'text-right font-medium')}>{t('salaControl.rincones.cuentas.ventas')}</th>
                <th className={cn(CIFRA, 'text-right font-medium')}>{t('salaControl.rincones.cuentas.unlock')}</th>
                <th className={cn(CIFRA, 'text-right font-medium')}>{t('salaControl.rincones.cuentas.fans')}</th>
                <th className={cn(CIFRA, 'text-center font-medium')}>{t('salaControl.rincones.cuentas.ultima')}</th>
                <th className={cn(CIFRA, 'pr-0 text-left font-medium')}>{t('salaControl.rincones.cuentas.modelo')}</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((x) => (
                <tr key={x.id} className="border-t border-oficina-linea">
                  {/* max-w-0 + truncate: la cuenta se lleva el ancho que sobra y un nombre largo se corta con «…» (no parte la fila). */}
                  <td className="max-w-0 truncate py-2" title={nombreCuenta(x)}>
                    <span
                      className={cn(
                        'mr-2 inline-block h-2 w-2 rounded-full',
                        x.parada ? 'bg-oficina-error' : x.callada ? 'bg-oficina-bloqueado' : x.respuestasUltimaHora ? 'bg-oficina-trabajando' : 'bg-oficina-esperando',
                      )}
                    />
                    {nombreCuenta(x)}
                    {x.usuario && x.nombre ? <span className="ml-1.5 text-o-nota text-oficina-texto3">{`@${x.usuario}`}</span> : null}
                  </td>
                  <td className={cn(CIFRA, 'text-right font-semibold')}>{dinero(x.ventas)}</td>
                  <td className={cn(CIFRA, 'text-right')}>{x.ppvEnviados ? pct(de(x.ppvComprados, x.ppvEnviados)) : SIN_VALOR}</td>
                  <td className={cn(CIFRA, 'text-right')}>{numero(x.fansChatean)}</td>
                  <td className={cn(CIFRA, 'text-center')}>{horaCorta(x.ultimaRespuesta)}</td>
                  <td className={cn(CIFRA, 'pr-0')} title={x.modelo ?? undefined}>
                    {x.modelo ? nombreModelo(x.modelo) : SIN_VALOR}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!encontradas && filas.length > FILAS_A_LA_VISTA ? <VerTodas total={filas.length} abiertas={todas} alCambiar={() => setTodas((x) => !x)} /> : null}
          </>
        ) : (
          <p className="py-4 text-o-texto text-oficina-texto3">{t('salaControl.rincones.cuentas.sinResultados')}</p>
        )}
      </Caja>

      <ListaCuentas titulo={t('salaControl.rincones.cuentas.calladasLista')} sub={t('salaControl.rincones.cuentas.calladasAyuda')} cuentas={c.listaCalladas} total={c.calladas} />
      <ListaCuentas titulo={t('salaControl.rincones.cuentas.paradasLista')} sub={t('salaControl.rincones.cuentas.paradasAyuda')} cuentas={c.listaParadas} total={c.paradas} />
    </div>
  );
}

export const RINCONES: Record<NombreRinconSala, () => ReactNode> = {
  dinero: () => <Dinero />,
  gasto: () => <Gasto />,
  fans: () => <Fans />,
  seguridad: () => <Seguridad />,
  maquina: () => <Maquina />,
  cuentas: () => <Cuentas />,
  cerebros: () => <Cerebros />,
  obra: () => <Obra />,
  taller: () => <Taller />,
};
